/**
 * GitConnector — git repository filesystem-face connector.
 * Mounts a git repo by cloning/pulling to a local directory.
 * Session-aware: auto-branching, lifecycle hooks (hibernate/close), periodic fetch/rebase.
 *
 * Everything in this file that touches `mountPath` is asynchronous and
 * deadline-bounded. Inside the agent container that path is the rclone FUSE
 * mount, and a synchronous `execSync` / `fs` call there parks the entire
 * single-threaded event loop in an uninterruptible kernel wait — a hang, not an
 * error, so `try/catch` never fires and the whole session dies (issue #454).
 */

import { execFile } from "node:child_process";
import { mkdir, readdir, readFile, rename, rm, stat, writeFile } from "node:fs/promises";
import { dirname, join, resolve } from "node:path";
import { promisify } from "node:util";
import type {
  ConnectContext,
  ConnectorChangeEvent,
  ConnectorDeclaration,
  ConnectorHandle,
  FilesystemFace,
  MountAuthRef,
  OperationDescriptor,
  SecretProvider,
  SyncOptions,
  SyncResult,
  ToolFace,
  TokenMediatorResult,
  WatchHandle,
  WatchOptions,
} from "@skaile/workspaces/connectors";
import { tryWithFsDeadline, withFsDeadline } from "@skaile/workspaces/core";
import { createLogger } from "@skaile/workspaces/core/logging";
import type { Logger } from "@skaile/workspaces/types";
import {
  AbstractConnector,
  atomicReplaceCredential,
  createFsWatcher,
  ensureFleetMounted,
  ensureRefreshAttemptStamp,
  isFleetManaged,
  removeMountBlock,
  removeUnavailableMountBlock,
  renderCredentialHelperScript,
  resolveAuthRef,
  writeHelperScript,
  writeMountBlock,
  writeUnavailableMountBlock,
} from "@skaile/workspaces/connectors";

/** New unified TokenMediator shape — uses `connectorId` (renamed from `mountId` in Task 1). */
type ConnectorTokenMediator = NonNullable<ConnectContext["tokenMediator"]>;

// ── Session/sync/lifecycle config parsed from declaration.options ────────────

interface GitSessionConfig {
  enabled: boolean;
  slug?: string;
  isMain: boolean;
  branchPrefix: string;
  /**
   * Starting point for a freshly-created session branch. When set and the
   * session branch does not already exist on origin, the driver fetches this
   * ref and branches from it instead of from the base branch tip. Used by
   * the platform's fork flow so a forked session branches off the source
   * session's tip rather than off `main`. May be a branch name (e.g.
   * `skaile/session-source-slug`) or a commit SHA. Ignored when the session
   * branch already exists on origin (existing branch wins). No-op when
   * `enabled = false` or `isMain = true` (only branch sessions cut new
   * branches). The value is validated against `isValidGitRefOrSha` before
   * use; refs with shell metacharacters are rejected and the driver falls
   * back to branching off base.
   */
  fromRef?: string;
}

interface GitSyncConfig {
  fetchIntervalSec: number;
  autoRebase: boolean;
  autoPull: boolean;
}

interface GitLifecycleConfig {
  commitOnHibernate: boolean;
  pushOnHibernate: boolean;
  mergeOnClose: boolean;
  pushOnClose: boolean;
  /** Auto-commit after N ms of no file changes. 0 = disabled. Default: 30000 (30s). */
  autoCommitIdleMs: number;
  /** Push to remote after each auto-commit. */
  pushAfterCommit: boolean;
  /** Push freshly-created session branch to origin on connect. Best-effort — push
   *  failures are swallowed; lifecycle push hooks remain the backstop. */
  publishOnCreate: boolean;
}

interface GitState {
  /** URL of the remote repository. */
  url: string;
  /** Base branch to track (e.g. "main"). */
  branch: string;
  /** Active branch (may differ from base when session branching is active). */
  activeBranch: string;
  /** Auth token for HTTPS URLs. */
  token?: string;
  /** Session branching config. */
  session: GitSessionConfig;
  /** Periodic fetch/rebase config. */
  sync: GitSyncConfig;
  /** Lifecycle hook config. */
  lifecycle: GitLifecycleConfig;
  /** Periodic fetch timer ID. */
  fetchTimer?: ReturnType<typeof setInterval>;
  /** Auto-commit idle timer ID. */
  autoCommitTimer?: ReturnType<typeof setTimeout>;
  /**
   * True while a periodic-fetch tick is still running. Now that every tick
   * awaits real git work, a mount slow enough to outlast the interval would
   * otherwise stack overlapping fetch/rebase/pull runs against the same
   * checkout — each one contending for `.git/index.lock`, and each one holding
   * another git child on the wedged mount.
   */
  fetchInFlight?: boolean;
  /** True while an auto-commit tick is still running. Same rationale as {@link fetchInFlight}. */
  autoCommitInFlight?: boolean;
  /**
   * Set by `disconnect()`. Timers are cleared there too, but a tick already
   * awaiting a git child cannot be cancelled — it checks this flag at every
   * resumption point and abandons its remaining work instead of writing to a
   * checkout the manager has already torn down.
   */
  disposed?: boolean;
  /** Callback for emitting change events (set by watch()). */
  emitChange?: (event: ConnectorChangeEvent) => void;
  /**
   * Tier-2 managed-gitconfig paths populated when `exposeAccessToken` is on.
   * Cleared on `disconnect`/`onSessionClose` so the credential is removed
   * promptly. Absent for Tier-1 mounts.
   */
  managedGitconfig?: {
    gitconfigPath: string;
    credentialsPath: string;
    urlPrefix: string;
  };
  /**
   * Token mediator captured at connect time for use by refresh ticks. Only
   * populated for `auth: backend` connectors; absent for `pat:` and anonymous.
   */
  tokenMediator?: ConnectorTokenMediator;
  /**
   * Active refresh timer. Set only when the resolved token came from the
   * backend mediator and carried a finite expiry. Cleared on every
   * reschedule and on teardown.
   */
  refreshTimer?: ReturnType<typeof setTimeout>;
  /** Checkout path, retained so the Tier-1 refresh tick can re-bake origin. */
  targetDir?: string;
  /** True when the host fleet owns clone/pull/commit/push for this mount. */
  fleetManaged?: boolean;
  /** Auto-started watcher in fleet mode (no auto-commit timer); disposed in `disconnect`. */
  fleetWatcher?: WatchHandle;
}

/** Per-block defaults the `parse*Config` helpers fall back to when an option is absent. */
interface GitConfigDefaults {
  session: { enabled: boolean };
  sync: { fetchIntervalSec: number; autoRebase: boolean; autoPull: boolean };
  lifecycle: {
    commitOnHibernate: boolean;
    pushOnHibernate: boolean;
    mergeOnClose: boolean;
    pushOnClose: boolean;
    autoCommitIdleMs: number;
    pushAfterCommit: boolean;
    publishOnCreate: boolean;
  };
}

/**
 * Legacy / no-mode defaults — the exact literals this driver used before `mode`
 * existed, so behavior is unchanged when a declaration carries no `mode`.
 */
const LEGACY_DEFAULTS: GitConfigDefaults = {
  session: { enabled: false },
  sync: { fetchIntervalSec: 0, autoRebase: false, autoPull: false },
  lifecycle: {
    commitOnHibernate: true,
    pushOnHibernate: true,
    mergeOnClose: true,
    pushOnClose: true,
    autoCommitIdleMs: 30_000,
    pushAfterCommit: false,
    publishOnCreate: true,
  },
};

/** Per-mode default tables. `agent` lets the agent drive git; `sync` makes the connector own the working copy. */
const MODE_DEFAULTS: Record<"agent" | "sync", GitConfigDefaults> = {
  agent: {
    session: { enabled: true },
    sync: { fetchIntervalSec: 0, autoRebase: false, autoPull: false },
    lifecycle: {
      ...LEGACY_DEFAULTS.lifecycle,
      // Agent owns its own merge — connector must not auto-merge the session branch.
      mergeOnClose: false,
      autoCommitIdleMs: 0,
      pushAfterCommit: false,
    },
  },
  sync: {
    session: { enabled: false },
    sync: { fetchIntervalSec: 30, autoRebase: true, autoPull: true },
    lifecycle: {
      ...LEGACY_DEFAULTS.lifecycle,
      autoCommitIdleMs: 30_000,
      pushAfterCommit: true,
    },
  },
};

function parseSessionConfig(
  opts: Record<string, unknown> | undefined,
  defaults: GitConfigDefaults["session"],
): GitSessionConfig {
  const raw = (opts?.session as Record<string, unknown>) ?? {};
  return {
    enabled: Boolean(raw.enabled ?? defaults.enabled),
    slug: raw.slug != null ? String(raw.slug) : undefined,
    isMain: Boolean(raw.isMain ?? true),
    branchPrefix: String(raw.branchPrefix ?? "session/"),
    fromRef: raw.fromRef != null ? String(raw.fromRef) : undefined,
  };
}

function parseSyncConfig(
  opts: Record<string, unknown> | undefined,
  defaults: GitConfigDefaults["sync"],
): GitSyncConfig {
  const raw = (opts?.sync as Record<string, unknown>) ?? {};
  return {
    fetchIntervalSec: Number(raw.fetchIntervalSec ?? defaults.fetchIntervalSec),
    autoRebase: Boolean(raw.autoRebase ?? defaults.autoRebase),
    autoPull: Boolean(raw.autoPull ?? defaults.autoPull),
  };
}

function parseLifecycleConfig(
  opts: Record<string, unknown> | undefined,
  defaults: GitConfigDefaults["lifecycle"],
): GitLifecycleConfig {
  const raw = (opts?.lifecycle as Record<string, unknown>) ?? {};
  return {
    commitOnHibernate: Boolean(raw.commitOnHibernate ?? defaults.commitOnHibernate),
    pushOnHibernate: Boolean(raw.pushOnHibernate ?? defaults.pushOnHibernate),
    mergeOnClose: Boolean(raw.mergeOnClose ?? defaults.mergeOnClose),
    pushOnClose: Boolean(raw.pushOnClose ?? defaults.pushOnClose),
    autoCommitIdleMs: Number(raw.autoCommitIdleMs ?? defaults.autoCommitIdleMs),
    pushAfterCommit: Boolean(raw.pushAfterCommit ?? defaults.pushAfterCommit),
    publishOnCreate: Boolean(raw.publishOnCreate ?? defaults.publishOnCreate),
  };
}

/**
 * Resolve the session/sync/lifecycle config blocks for a git connector. `mode`
 * seeds per-block defaults (`agent` / `sync`); explicit `options.{session,sync,
 * lifecycle}` keys still override those defaults. Absent `mode` reproduces the
 * legacy literals exactly, so behavior is unchanged for declarations that set none.
 */
function resolveGitConfig(args: { mode?: "agent" | "sync"; options?: Record<string, unknown> }): {
  session: GitSessionConfig;
  sync: GitSyncConfig;
  lifecycle: GitLifecycleConfig;
} {
  // `mode` arrives unvalidated from YAML (connectors are cast, not Zod-parsed);
  // an unknown value would index MODE_DEFAULTS as undefined and crash below.
  let mode = args.mode;
  if (mode && !(mode in MODE_DEFAULTS)) {
    modeLog.warn("unknown git mode — falling back to legacy defaults", { mode });
    mode = undefined;
  }
  const defaults = mode ? MODE_DEFAULTS[mode] : LEGACY_DEFAULTS;
  return {
    session: parseSessionConfig(args.options, defaults.session),
    sync: parseSyncConfig(args.options, defaults.sync),
    lifecycle: parseLifecycleConfig(args.options, defaults.lifecycle),
  };
}

/** Free-function diagnostics for `resolveGitConfig` (no `this.log` in scope). */
const modeLog = createLogger({ kind: "connector", subkind: "git" });

// ── Git helpers ──────────────────────────────────────────────────────────────

const execFileAsync = promisify(execFile);

/**
 * Strip URL userinfo (`//x-access-token:<token>@host`) out of arbitrary text.
 *
 * `execFileAsync` builds its rejection message as `Command failed: <argv…>`,
 * so any git failure whose argv carried the auth URL puts a live token in
 * `err.message`. Nothing downstream redacts: the log store writes SQLite on
 * disk, stdout, and forwards to the platform gateway.
 */
function redactUrlAuth(text: string): string {
  return text.replace(/\/\/[^/@\s]*:[^/@\s]*@/g, "//***@");
}

/**
 * Copy of `err` with the auth URL scrubbed from its message and stack, for
 * handing to a logger. The original is still what callers rethrow — only the
 * logged view is redacted.
 */
function redactError(err: unknown): Error {
  const original = err instanceof Error ? err : new Error(String(err));
  const copy = new Error(redactUrlAuth(original.message));
  copy.name = original.name;
  copy.stack = original.stack === undefined ? undefined : redactUrlAuth(original.stack);
  return copy;
}

/**
 * Identity stamped on the root commit that bootstraps a ref-less remote.
 * Passed per-command rather than written to config: this commit is a machine
 * action taken at connect time, before the agent (or the image) has configured
 * any identity of its own, and an unset ambient one would abort the commit.
 */
const BOOTSTRAP_COMMIT_NAME = "Skaile";
const BOOTSTRAP_COMMIT_EMAIL = "skaile@local";

/**
 * Leading argv carried by every driver-spawned `git` invocation.
 *
 * `safe.directory` is required because the checkout is routinely owned by a
 * different uid than the agent process (bind-mounted host dir, fleet worktree).
 * Passed as argv rather than interpolated into a shell string, so the value
 * needs no quoting and can never be re-split on spaces or quotes.
 */
function gitPrefix(cwd: string): string[] {
  return ["-c", `safe.directory=${cwd}`];
}

/**
 * Spawn `git` with an explicit argv and await its trimmed stdout.
 *
 * Async by construction: these commands run against `mountPath`, which inside
 * the agent container is the rclone FUSE mount. `execSync` there stops the
 * event loop for the whole process, so a wedged mount took down every session
 * rather than one operation (issue #454).
 *
 * `killSignal: 'SIGKILL'` matters as much as the timeout it accompanies. A git
 * child blocked on a dead FUSE connection sits in uninterruptible `D` state,
 * where SIGTERM is never delivered — the default kill would let the timeout
 * expire without ever reaping the child. The timeout bounds the healthy-but-slow
 * case; SIGKILL is what makes it bite on the pathological one.
 */
async function runGit(args: string[], options: { cwd?: string; timeout: number }): Promise<string> {
  const { stdout } = await execFileAsync("git", args, {
    encoding: "utf-8",
    timeout: options.timeout,
    killSignal: "SIGKILL",
    ...(options.cwd === undefined ? {} : { cwd: options.cwd }),
  });
  return stdout.trim();
}

/**
 * Run a git subcommand inside `cwd`.
 *
 * `args` is an argv array, never a shell string — there is no shell in the
 * chain, so nothing is word-split, glob-expanded, or quote-stripped.
 */
function git(args: string[], cwd: string, timeout = 30_000): Promise<string> {
  return runGit([...gitPrefix(cwd), ...args], { cwd, timeout });
}

/**
 * Best-effort existence probe, replacing `existsSync`.
 *
 * A deadline expiry and a genuine absence are deliberately collapsed into
 * `false`: every caller here follows up with an idempotent operation (mkdir
 * -p, a rename that is skipped, a fallback read), so "cannot tell" and "not
 * there" lead to the same safe branch.
 */
function pathExists(path: string): Promise<boolean> {
  return tryWithFsDeadline(`stat ${path}`, () => stat(path).then(() => true), false);
}

/**
 * Longest deadline any git child of this driver runs under (clone, submodule
 * update). A lock older than this cannot belong to a live child of ours —
 * every one is SIGKILL'd once its own timeout expires.
 */
const MAX_GIT_CHILD_TIMEOUT_MS = 120_000;

/**
 * Drop a `.git/index.lock` orphaned by a SIGKILL'd git child.
 *
 * `runGit` kills on SIGKILL by design, which gives git no chance to release the
 * lock, and the checkout outlives the container that made it — so one killed
 * child wedges every later `connect()` on the same repo, permanently. Measured
 * in prod: two sessions dead for 23 and 4 days, recoverable only by hand.
 *
 * Called from the connect path alone, which rules out this driver's own git:
 * the periodic timers arm only once connect returns, and a fleet-managed mount
 * (where the host owns git against this very path) takes another branch
 * entirely. The mtime gate covers what ordering does not — a previous
 * container still shutting down, and, on the hot-plug connect of a live
 * session, git the AGENT is running, which is under no deadline of ours.
 */
async function clearStaleIndexLock(targetDir: string, log: Logger): Promise<void> {
  let lockPath: string;
  try {
    // `rev-parse --git-path` takes no lock of its own, so it answers even while
    // the index is locked, and resolves correctly where `.git` is a FILE
    // (worktree, submodule) rather than a directory.
    lockPath = resolve(
      targetDir,
      await git(["rev-parse", "--git-path", "index.lock"], targetDir, 5_000),
    );
  } catch {
    return;
  }
  const mtimeMs = await tryWithFsDeadline<number | null>(
    `stat ${lockPath}`,
    () => stat(lockPath).then((s) => s.mtimeMs),
    null,
  );
  if (mtimeMs === null) {
    return;
  }
  // Too young to prove abandoned: leave it. Git builds the new index in the
  // lock and renames it over `index`, so removing one a live git still holds
  // breaks that operation rather than corrupting the repo — but breaking the
  // agent's commit to fix our own wedge is not a trade worth making.
  const ageMs = Date.now() - mtimeMs;
  if (ageMs < MAX_GIT_CHILD_TIMEOUT_MS) {
    return;
  }
  const removed = await tryWithFsDeadline(
    `rm ${lockPath}`,
    () => rm(lockPath, { force: true }).then(() => true),
    false,
  );
  if (removed) {
    log.warn("removed an abandoned .git/index.lock", { lockPath, ageMs });
  }
}

// Best-effort: align nested submodule checkouts with the parent's pinned SHAs
// after any ref-changing operation (clone bootstrap, pull, branch switch).
// `--recurse-submodules` on the initial clone handles the first checkout; this
// covers every subsequent state change. No-op when `.gitmodules` is absent.
async function syncSubmodules(cwd: string, log: Logger): Promise<void> {
  if (!(await pathExists(join(cwd, ".gitmodules")))) return;
  try {
    await git(["submodule", "update", "--init", "--recursive"], cwd, 120_000);
  } catch (err) {
    log.warn("git submodule update failed — checkout may be incomplete", {
      error: redactUrlAuth(err instanceof Error ? err.message : String(err)),
    });
  }
}

/**
 * Conservative refname / SHA validator. Gates any externally-supplied ref
 * before it reaches a `git` invocation. Accepts a subset of
 * git-check-ref-format(1): forbids whitespace, shell metacharacters, control
 * chars, leading `-` (so the value cannot be misread as a flag), `..`, `@{`,
 * and a trailing `.lock`. Hex SHAs (40 chars) pass through.
 *
 * The shell is gone now that every invocation passes argv, so the
 * metacharacter rejection is defence in depth rather than the only barrier.
 * It stays: the leading-`-` rule still prevents argument-injection, and
 * callers depend on the documented fall-back-to-base behaviour for a
 * rejected ref.
 */
function isValidGitRefOrSha(ref: string): boolean {
  if (ref.length === 0 || ref.length > 255) return false;
  // Rejecting control chars is the whole point of this validator — Biome's
  // noControlCharactersInRegex rule does not apply here.
  // biome-ignore lint/suspicious/noControlCharactersInRegex: intentional control-char rejection
  if (/[\s\\~^:?*[\x00-\x1f\x7f`$;&|'"<>()]/.test(ref)) return false;
  if (ref.startsWith("-") || ref.endsWith(".lock") || ref.endsWith("/") || ref.endsWith("."))
    return false;
  if (ref.includes("..") || ref.includes("@{")) return false;
  return true;
}

async function hasChanges(cwd: string): Promise<boolean> {
  const status = await git(["status", "--porcelain"], cwd);
  return status.length > 0;
}

/** Run a git subcommand, returning trimmed stdout or `null` on any failure. */
async function gitOrNull(args: string[], cwd: string): Promise<string | null> {
  try {
    return await git(args, cwd);
  } catch {
    return null;
  }
}

/**
 * Parse `git rev-list --left-right --count <ref>...HEAD` into ahead/behind.
 * Left count = commits only on `<ref>` (behind), right count = commits only on
 * HEAD (ahead). Returns `{0,0}` when the ref is missing (e.g. no upstream).
 */
async function countAheadBehind(
  cwd: string,
  remoteRef: string,
): Promise<{ ahead: number; behind: number }> {
  const out = await gitOrNull(["rev-list", "--left-right", "--count", `${remoteRef}...HEAD`], cwd);
  if (!out) {
    return { ahead: 0, behind: 0 };
  }
  const parts = out.split(/\s+/);
  const behind = Number.parseInt(parts[0] ?? "0", 10);
  const ahead = Number.parseInt(parts[1] ?? "0", 10);
  return {
    ahead: Number.isFinite(ahead) ? ahead : 0,
    behind: Number.isFinite(behind) ? behind : 0,
  };
}

/**
 * Snapshot returned by the `sync_status` tool op. JSON-serialised over the
 * transport; the platform overlays `available`/`containerAwake`/`reason`
 * before handing it to the UI badge.
 */
export interface GitSyncStatusResult {
  /** Current branch name, or `null` (detached HEAD / unavailable). */
  branch: string | null;
  /** Working tree has uncommitted changes (`git status --porcelain` non-empty). */
  dirty: boolean;
  /** Commits on HEAD not on its upstream — i.e. unpushed. */
  ahead: number;
  /** Commits on the upstream not on HEAD — i.e. unpulled (since last fetch). */
  behind: number;
  /**
   * Whether the current branch has a configured upstream. `false` ⇒ the branch
   * has never been pushed; `ahead`/`behind` read as 0 (no remote-tracking ref
   * to diff against), so this distinguishes "never pushed" from "synced".
   */
  hasUpstream: boolean;
  /** Commits on HEAD not on `origin/<baseBranch>` — diverged ahead of base. */
  mainAhead: number;
  /** Commits on `origin/<baseBranch>` not on HEAD — diverged behind base. */
  mainBehind: number;
  /** Current HEAD commit SHA, or `null` when unavailable. */
  commitSha: string | null;
}

/**
 * Read git sync status for an in-container checkout. Pure and never throws —
 * every git call degrades to a safe default. No network fetch:
 * ahead/behind reflect locally-known remote-tracking refs ("since last sync").
 * `baseBranch` is the mount's tracked base (e.g. "main"); base divergence is
 * measured against `origin/<baseBranch>`.
 *
 * Asynchronous since v3: every `git` call it makes runs against a path that may
 * be a network mount, and the synchronous version blocked the event loop there.
 */
export async function readGitSyncStatus(
  cwd: string,
  baseBranch: string,
): Promise<GitSyncStatusResult> {
  const branchRaw = await gitOrNull(["rev-parse", "--abbrev-ref", "HEAD"], cwd);
  const branch = branchRaw && branchRaw !== "HEAD" ? branchRaw : null;

  const dirty = ((await gitOrNull(["status", "--porcelain"], cwd)) ?? "") !== "";

  // A branch with no configured upstream has never been pushed. Detected
  // separately because countAheadBehind can't tell "no upstream" (rev-list
  // fails → 0/0) from "in sync with upstream" (also 0/0). rev-parse exits
  // non-zero when @{upstream} is unset; only success/failure matters here.
  const hasUpstream =
    branch !== null &&
    (await gitOrNull(["rev-parse", "--abbrev-ref", "@{upstream}"], cwd)) !== null;

  const upstream = await countAheadBehind(cwd, "@{upstream}");

  const baseRef = isValidGitRefOrSha(baseBranch) ? `origin/${baseBranch}` : "origin/main";
  const base = await countAheadBehind(cwd, baseRef);

  const commitSha = await gitOrNull(["rev-parse", "HEAD"], cwd);

  return {
    branch,
    dirty,
    ahead: upstream.ahead,
    behind: upstream.behind,
    hasUpstream,
    mainAhead: base.ahead,
    mainBehind: base.behind,
    commitSha,
  };
}

/**
 * True when `branch` exists as a local head in the checkout. Used to reuse a
 * session branch a prior connect created but never pushed — `checkout -b` would
 * otherwise abort with "already exists" and fail the connector. `branch` must
 * already be refname-validated before it reaches this `git` call.
 */
async function localBranchExists(branch: string, cwd: string): Promise<boolean> {
  try {
    await git(["rev-parse", "--verify", "--quiet", `refs/heads/${branch}`], cwd);
    return true;
  } catch {
    return false;
  }
}

/**
 * True when `dir` is empty (no entries, including no dotfiles) or when the
 * listing fails or misses its deadline (e.g. dir does not exist, wedged
 * mount). The "fails → empty" mapping is intentional: the caller is about to
 * `mkdir` and proceed, so a missing dir is functionally equivalent to an
 * empty one.
 */
async function isDirEmpty(dir: string): Promise<boolean> {
  const entries = await tryWithFsDeadline<string[]>(`readdir ${dir}`, () => readdir(dir), []);
  return entries.length === 0;
}

async function commitAll(cwd: string, message: string): Promise<string | null> {
  if (!(await hasChanges(cwd))) return null;
  await git(["add", "-A"], cwd);
  return git(["commit", "-m", message], cwd);
}

/**
 * Structured error used when the backend credential mediator refuses or
 * cannot deliver a token. Carries the wire-format error code so callers
 * (connector manager, retry loop, audit log) can branch on it.
 *
 * @docLink packages/factory-assets/concepts#git-connector-mediation
 */
class TokenMediatorError extends Error {
  readonly code: TokenMediatorResult extends infer R
    ? R extends { ok: false; code: infer C }
      ? C
      : never
    : never;
  constructor(
    message: string,
    code: "not-configured" | "revoked" | "provider-error" | "backend-error" | "timeout",
    connectorId: string,
  ) {
    super(`Git connector '${connectorId}': backend mediator ${code} — ${message}`);
    this.name = "TokenMediatorError";
    this.code = code as never;
  }
}

// ── Connector ──────────────────────────────────────────────────────────────────

/**
 * Filesystem-face connector for git repositories — clones or pulls on connect, supports
 * session branching, periodic fetch/rebase, and lifecycle hooks (hibernate commit/push,
 * session close merge). Also supports Tier-2 managed credential injection via
 * `GIT_CONFIG_GLOBAL` for agent-facing `git` CLI access.
 *
 * Token acquisition for `auth: backend` connectors is mediated by the runner: the
 * connector calls the injected `tokenMediator` callback (which sends a
 * `request_access_token` command over the transport and awaits the matching
 * `access_token_response` event). Local provider minting (OAuth, GitHub App)
 * has been removed — the platform owns dispatch.
 *
 * @docLink packages/factory-assets/concepts#git-connector
 */
export class GitConnector extends AbstractConnector {
  readonly name = "git";

  readonly filesystem: FilesystemFace = {
    sync: (handle: ConnectorHandle, options?: SyncOptions): Promise<SyncResult> => {
      return this._sync(handle, options);
    },
  };

  readonly tools: ToolFace = {
    describeOperations: (handle: ConnectorHandle): OperationDescriptor[] =>
      this._describeOperations(handle),
    executeOp: (
      handle: ConnectorHandle,
      operation: string,
      args: Record<string, unknown>,
    ): Promise<string> => this._executeOp(handle, operation, args),
  };

  private _describeOperations(_handle: ConnectorHandle): OperationDescriptor[] {
    return [
      {
        name: "sync_status",
        description:
          "Report git sync status for this mount: current branch, dirty flag, ahead/behind " +
          "vs the upstream, whether the branch has a configured upstream (never-pushed when " +
          "false), and divergence vs the base branch. Read-only; performs no fetch.",
        accessLevel: "read",
        args: [],
        examples: ["sync_status"],
      },
    ];
  }

  private async _executeOp(
    handle: ConnectorHandle,
    operation: string,
    _args: Record<string, unknown>,
  ): Promise<string> {
    switch (operation) {
      case "sync_status": {
        const cwd = handle.mountPath;
        if (!cwd) {
          throw new Error("git sync_status: mount path unavailable");
        }
        const state = handle.state as GitState;
        return JSON.stringify(await readGitSyncStatus(cwd, state.branch));
      }
      default:
        throw new Error(`GitConnector has no operation: ${operation}`);
    }
  }

  async connect(declaration: ConnectorDeclaration, ctx: ConnectContext): Promise<ConnectorHandle> {
    const targetDir = ctx.mountTarget;
    if (!targetDir) {
      throw new Error("GitConnector requires ctx.mountTarget to be set by the manager");
    }

    // Host bind-mounts the worktree; in-container driver must not touch the git lifecycle.
    if (isFleetManaged(declaration.id)) {
      return this.connectFleetManaged(declaration, targetDir);
    }

    const secrets = ctx.secrets;
    const tokenMediator = ctx.tokenMediator;

    const started = Date.now();
    const opts = declaration.options;
    const url = String(declaration.mount?.source ?? "");
    const branch = String(opts?.branch ?? "main");
    let credentialScopeUrl = url;
    let credentialAlignmentAttempted = false;

    if (!url) {
      this.log.error("connect failed: missing mount.source URL", undefined);
      throw new Error("Git connector requires a mount.source URL");
    }

    // `branch` interpolates into shell-form `git clone --branch ...` and later
    // pull/push commands. Reject anything carrying shell metacharacters.
    if (!isValidGitRefOrSha(branch)) {
      throw new Error(`Git connector: invalid branch name "${branch}"`);
    }

    try {
      this.log.info("mount start", { url, branch, targetDir });

      // Before any git network op. A previous session's "unavailable" block is
      // still registered at this point, and `prepareCheckout` /
      // `checkoutSessionBranch` fetch and push against an origin whose inline
      // credentials the last session stripped — so they consult the helper and
      // would be refused with `quit=1`. Both swallow that as a warning, which
      // is how a recovering session would silently branch off the wrong base
      // and never publish its session branch. A Tier-1 mount also depends on
      // this call: it writes no block of its own, so nothing else would ever
      // clear the dead one.
      this.clearUnavailableCredentialHelper(declaration.id, url);

      // Resolve auth ref and (when present) the initial token. For `backend`
      // refs the token comes from the platform mediator; for `pat:env:NAME`
      // it falls back to the secrets chain. Anonymous (no `auth:`) connectors
      // remain supported for public repos.
      const initial = await this.acquireInitialToken(declaration, secrets, tokenMediator);

      const { session, sync, lifecycle } = resolveGitConfig({
        mode: declaration.mode,
        options: opts,
      });

      const state: GitState = {
        url,
        branch,
        activeBranch: branch,
        token: initial.token,
        session,
        sync,
        lifecycle,
        targetDir,
        tokenMediator: initial.authRef?.kind === "backend" ? tokenMediator : undefined,
      };

      // Clone or pull
      const authUrl = this.authUrl(state);
      await this.prepareCheckout(targetDir, url, branch, authUrl, declaration.access, started);

      // Session branching: create and checkout a session branch for non-main sessions
      if (session.enabled && !session.isMain && session.slug) {
        await this.checkoutSessionBranch(state, session, targetDir);
      }

      // Start periodic fetch if configured
      if (sync.fetchIntervalSec > 0) {
        this.startPeriodicFetch(state, targetDir);
      }

      // Tier-2: when `exposeAccessToken` is on (in declaration.mount),
      // wire the resolved credential into a managed-gitconfig file so the
      // agent's interactive `git` can authenticate against this host.
      // Path comes from `GIT_CONFIG_GLOBAL` set by the runner; absent in
      // CLI/standalone contexts (Tier-1 stays the default there).
      if (declaration.mount?.exposeAccessToken === true) {
        try {
          if (
            initial.token &&
            (url.startsWith("https://") || url.startsWith("http://")) &&
            process.env.GIT_CONFIG_GLOBAL
          ) {
            credentialAlignmentAttempted = true;
            credentialScopeUrl = await this.alignRemoteWithCredentialScope(targetDir, url);
          }
          this.exposeManagedCredential(declaration, state, credentialScopeUrl, initial);
        } catch (err) {
          this.log.error("managed-gitconfig write failed", err, { connectorId: declaration.id });
          throw err;
        }
      }

      // Reconcile origin auth on every connect — stale baked tokens persist across wakes.
      // Tier-2 was aligned with its helper scope above. Tier-1 re-bakes the freshly
      // minted token so a woken session does not push with a dead one.
      if (!state.managedGitconfig) {
        await this.rebakeRemoteAuth(targetDir, state);
        // Tier-1 has no managed helper: the baked URL token is the sole credential
        // and dies at its ~1h TTL on a session that never hibernates. Schedule the
        // same rotation tick as Tier-2, re-baking the fresh token each time.
        if (initial.authRef?.kind === "backend" && initial.expiresAt && state.tokenMediator) {
          this.scheduleRefresh(declaration.id, state, initial.expiresAt);
        }
      }

      this.log.info("mount ok", {
        targetDir,
        activeBranch: state.activeBranch,
        durationMs: Date.now() - started,
      });
      return this.makeHandle(declaration, state, targetDir);
    } catch (err) {
      // A failed connect leaves no credential helper registered for this host at
      // all, so the agent's next `git push` dies with `could not read Username`
      // and no hint of the real cause. Register one that reports the reason.
      if (
        declaration.mount?.exposeAccessToken === true &&
        (url.startsWith("https://") || url.startsWith("http://")) &&
        process.env.GIT_CONFIG_GLOBAL &&
        !credentialAlignmentAttempted &&
        (await pathExists(join(targetDir, ".git")))
      ) {
        try {
          credentialAlignmentAttempted = true;
          credentialScopeUrl = await this.alignRemoteWithCredentialScope(targetDir, url);
        } catch (alignErr) {
          this.log.warn("failed to align origin for unavailable credential helper", {
            connectorId: declaration.id,
            error: alignErr instanceof Error ? alignErr.message : String(alignErr),
          });
        }
      }
      this.registerUnavailableCredentialHelper(declaration, credentialScopeUrl, err);
      throw err;
    }
  }

  /**
   * Bring `targetDir` into a usable checkout state for `connect()`.
   *
   * Minimal-connect policy: `connect()` never auto-syncs with the remote.
   * The lifecycle hooks (`pushOnHibernate`, `pushAfterCommit`,
   * `sync.autoPull`) and the explicit `filesystem.sync()` method are the
   * only paths that touch the network with the existing checkout. Recreating
   * a container is therefore idempotent — the on-disk dir is preserved.
   *
   * Three cases:
   *   - `.git` exists       → use as-is. No pull, no reset.
   *   - dir empty           → fresh clone (only network fetch on connect), or
   *     `bootstrapEmptyRemote` when the remote has no refs at all.
   *   - dir populated, no `.git` → `git init` + `remote add origin` so
   *     lifecycle hooks (commit/push) can do their job. No fetch, no
   *     reset, no clean. Existing files become uncommitted-on-init; the
   *     agent or `pushAfterCommit` reconciles with remote later.
   */
  private async prepareCheckout(
    targetDir: string,
    url: string,
    branch: string,
    authUrl: string,
    access: ConnectorDeclaration["access"],
    started: number,
  ): Promise<void> {
    try {
      if (await pathExists(join(targetDir, ".git"))) {
        // Before the first index-writing command below: an orphaned lock fails
        // every one of them, and nothing else in the product clears it.
        await clearStaleIndexLock(targetDir, this.log);
        if (await this.isInitialisedCheckout(targetDir)) {
          // Functional working checkout — leave it alone. Sync is the
          // lifecycle's job (push on hibernate, periodic fetch, etc.).
          this.log.info("found existing checkout — using as-is", { branch, targetDir });
        } else {
          // `.git` is present but has no resolved HEAD — typically the
          // residue of a prior `initInPlace` call (origin configured, no
          // objects fetched) or an aborted clone. Bootstrap content from
          // the remote *once*, then future connects hit the as-is path above.
          // A ref-less remote has nothing to fetch, so probe before trying:
          // `bootstrapFromOrigin` would die on `fetch origin <branch>`.
          if ((await this.countRemoteHeads(targetDir, authUrl)) === 0) {
            this.log.info("empty .git against a ref-less remote — bootstrapping branch", {
              branch,
              access,
            });
            await this.bootstrapEmptyRemote(targetDir, branch, authUrl, access);
          } else {
            this.log.info("found empty .git — bootstrapping from origin", { branch });
            await this.bootstrapFromOrigin(targetDir, branch, authUrl);
          }
          this.log.info("bootstrap ok", { branch, durationMs: Date.now() - started });
        }
      } else {
        await withFsDeadline(`mkdir ${targetDir}`, () => mkdir(targetDir, { recursive: true }));
        if (await isDirEmpty(targetDir)) {
          // A just-created GitHub repo has ZERO refs, so `clone --branch` can
          // only ever fail on it. Probing first keeps that failure loud for a
          // populated remote that is genuinely missing the branch.
          if ((await this.countRemoteHeads(targetDir, authUrl)) === 0) {
            this.log.info("remote has no refs — bootstrapping branch", { url, branch, access });
            await this.bootstrapEmptyRemote(targetDir, branch, authUrl, access);
            this.log.info("empty-remote bootstrap ok", {
              branch,
              durationMs: Date.now() - started,
            });
          } else {
            this.log.info("clone start", { url, branch });
            // No `cwd`: the clone creates `targetDir`'s content, so it must not
            // run from inside it. Matches the previous execSync invocation.
            await runGit(
              [
                ...gitPrefix(targetDir),
                "clone",
                "--branch",
                branch,
                "--single-branch",
                "--recurse-submodules",
                authUrl,
                targetDir,
              ],
              { timeout: 120_000 },
            );
            this.log.info("clone ok", { branch, durationMs: Date.now() - started });
          }
        } else {
          // Populated dir without `.git`. Typical causes: previous connect
          // aborted mid-clone; bind-mount of a worktree whose `.git` FILE
          // points outside the container. We DO NOT overwrite the files —
          // they are likely the user's last working state. We just bring
          // git tracking back online so lifecycle hooks work.
          this.log.warn("target non-empty without .git — initialising in place", { targetDir });
          await this.initInPlace(targetDir, authUrl);
          this.log.info("init in place ok", { durationMs: Date.now() - started });
        }
      }
    } catch (err) {
      // git puts the whole failing argv in its error message, and that argv
      // carries the auth URL. The log store persists to disk and forwards to
      // the platform gateway, so log a scrubbed copy and rethrow the original.
      this.log.error("connect preparation failed", redactError(err), { url, branch });
      throw err;
    }
  }

  /**
   * Create and check out the session branch for a non-main session, updating
   * `state.activeBranch`. Reuses an existing local branch (unpushed work);
   * else prefers `origin/<branch>`; otherwise branches off `fromRef` (platform
   * fork flow) when supplied and valid, else off the currently-checked-out
   * HEAD (== base branch).
   */
  private async checkoutSessionBranch(
    state: GitState,
    session: GitSessionConfig,
    targetDir: string,
  ): Promise<void> {
    const sessionBranch = `${session.branchPrefix}${session.slug}`;
    // Reject slug/prefix combinations that don't survive refname validation —
    // notably a leading `-`, which git would read as a flag.
    if (!isValidGitRefOrSha(sessionBranch)) {
      throw new Error(`Git connector: invalid session branch name "${sessionBranch}"`);
    }
    // Track whether this connect cut a brand-new branch that does not yet exist
    // on origin — only then do we publish-on-create. Reusing a local branch or
    // checking out an existing origin branch must not trigger an extra push.
    let created = false;
    if (await localBranchExists(sessionBranch, targetDir)) {
      // A prior connect created the branch locally but never pushed it — no
      // origin/<branch> tracking ref for the fast path, yet `checkout -b` would
      // abort with "already exists". Reuse it so unpushed work survives.
      await git(["checkout", sessionBranch], targetDir);
      this.log.info("reusing existing local session branch", { sessionBranch });
    } else {
      // Separate the existence probe from the checkout so the new-branch path
      // is reached only when origin genuinely lacks the branch — a failing
      // `checkout -B` on an existing origin branch must not flip `created`.
      let originHasBranch = false;
      try {
        await git(["rev-parse", "--verify", `origin/${sessionBranch}`], targetDir);
        originHasBranch = true;
      } catch {
        /* not on origin */
      }

      if (originHasBranch) {
        await git(["checkout", "-B", sessionBranch, `origin/${sessionBranch}`], targetDir);
      } else if (!(await this.branchFromRef(session, sessionBranch, targetDir))) {
        // No fromRef anchor (or its fetch failed) — branch off the
        // currently-checked-out HEAD (== base branch).
        await git(["checkout", "-b", sessionBranch], targetDir);
        created = true;
      } else {
        // branchFromRef cut the branch off the supplied fork anchor.
        created = true;
      }
    }
    await syncSubmodules(targetDir, this.log);
    state.activeBranch = sessionBranch;
    this.log.info("session branch checked out", { sessionBranch });

    if (created && state.lifecycle.publishOnCreate) {
      try {
        await git(["push", "-u", "origin", sessionBranch], targetDir, 60_000);
        this.log.info("published new session branch", { sessionBranch });
      } catch (err) {
        // Best-effort: offline / no-remote-write must not fail connect. The
        // lifecycle push hooks (hibernate/close) remain the backstop.
        this.log.warn("publish-on-create push failed; deferring to lifecycle push", {
          sessionBranch,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }

  /**
   * Attempt to create `sessionBranch` anchored on `session.fromRef`. Returns
   * true when the branch was created from the anchor; false when no valid
   * `fromRef` was supplied or the fetch failed (caller falls back to base).
   */
  private async branchFromRef(
    session: GitSessionConfig,
    sessionBranch: string,
    targetDir: string,
  ): Promise<boolean> {
    if (!session.fromRef) return false;
    if (!isValidGitRefOrSha(session.fromRef)) {
      // Defense in depth: refuse anything that doesn't pass refname/SHA
      // validation rather than handing an attacker-shaped ref to git.
      this.log.warn("fromRef rejected by refname validation, falling back to base branch", {
        sessionBranch,
        fromRef: session.fromRef,
      });
      return false;
    }
    try {
      await git(["fetch", "origin", session.fromRef], targetDir);
      await git(["checkout", "-b", sessionBranch, "FETCH_HEAD"], targetDir);
      this.log.info("session branch created from fromRef", {
        sessionBranch,
        fromRef: session.fromRef,
      });
      return true;
    } catch (err) {
      this.log.warn("fromRef fetch failed, falling back to base branch", {
        sessionBranch,
        fromRef: session.fromRef,
        error: err instanceof Error ? err.message : String(err),
      });
      return false;
    }
  }

  /**
   * Fleet-managed connect: verify the host bind, register an explorer watcher,
   * no-op every git lifecycle hook. Spec: `_devlog/specs/2026-05-28-git-fleet-mode-design.md`.
   */
  private async connectFleetManaged(
    declaration: ConnectorDeclaration,
    targetDir: string,
  ): Promise<ConnectorHandle> {
    this.log.info("mount handled by host fleet — skipping in-container git", {
      connectorId: declaration.id,
      targetDir,
    });
    if (declaration.mount?.exposeAccessToken) {
      // Host fleet owns credentials; managed-gitconfig is intentionally skipped.
      this.log.warn("fleet-managed: exposeAccessToken ignored — host fleet owns credentials", {
        connectorId: declaration.id,
      });
    }
    await ensureFleetMounted(targetDir, this.log);

    let activeBranch = "unknown";
    try {
      activeBranch = await git(["rev-parse", "--abbrev-ref", "HEAD"], targetDir, 5_000);
    } catch {
      // worktree may be on a detached HEAD or pre-init; non-fatal.
    }

    const fleetConfig = resolveGitConfig({
      mode: declaration.mode,
      options: declaration.options,
    });
    const state: GitState = {
      url: String(declaration.mount?.source ?? ""),
      branch: activeBranch,
      activeBranch,
      session: fleetConfig.session,
      sync: fleetConfig.sync,
      lifecycle: fleetConfig.lifecycle,
      fleetManaged: true,
    };

    const dispatch = (event: ConnectorChangeEvent): void => {
      state.emitChange?.(event);
    };
    // Await the watcher so a rapid disconnect cannot race past an unresolved handle.
    state.fleetWatcher = await createFsWatcher(targetDir, dispatch, { ignored: ["**/.git/**"] });

    this.log.info("fleet mount ready", { activeBranch, targetDir });
    return this.makeHandle(declaration, state, targetDir);
  }

  /**
   * Public refresh entry-point used by the runner's wake-mid-401 handler when
   * the credential-helper script touches the workspace refresh-flag. Calls
   * the same internal refresh path as the scheduled timer, but with
   * `reason: 'retry-401'` so the platform can distinguish proactive refreshes
   * from emergency ones in audit logs.
   *
   * No-op when:
   *  - the handle was not minted with `exposeAccessToken: true`
   *    (no `managedGitconfig` on state), or
   *  - the connector was not provisioned with a `tokenMediator` (PAT path —
   *    the script will keep serving the static token and the user must
   *    rotate the PAT manually).
   *
   * Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md` Step 9a.
   */
  async refreshExposedCredential(connectorId: string, handle: ConnectorHandle): Promise<void> {
    const s = handle.state as GitState;
    if (!s.managedGitconfig || !s.tokenMediator) return;
    const result = await s.tokenMediator({ connectorId, reason: "retry-401" });
    if (!result.ok) {
      this.log.warn("refresh-flag-driven mediation failed", {
        connectorId,
        code: result.code,
        message: result.message,
      });
      return;
    }
    // Re-read after the await: a concurrent disconnect may have removed the
    // managed credential. Since atomicReplaceCredential now upserts, writing
    // here would otherwise resurrect a credential line for a torn-down mount.
    if (!s.managedGitconfig) return;
    const { credentialsPath, urlPrefix } = s.managedGitconfig;
    atomicReplaceCredential({
      mountId: connectorId,
      urlPrefix,
      token: result.token,
      credentialsPath,
    });
    s.token = result.token;
    this.log.info("refresh", {
      event: "refresh",
      tier: 2,
      provider: "backend",
      scope: urlPrefix,
      reason: "retry-401",
      expiresAt: result.expiresAt,
      ...(result.scope ? { permissions: result.scope } : {}),
    });
    // Reschedule the proactive timer so the next pre-expiry tick still fires.
    if (result.expiresAt) {
      this.scheduleRefresh(connectorId, s, new Date(result.expiresAt));
    }
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("unmount");
    const s = handle.state as GitState;
    // Set before anything else: a fetch/auto-commit tick already awaiting a git
    // child cannot be cancelled, so it re-reads this at each resumption point
    // and drops its remaining work rather than acting on a dead handle.
    s.disposed = true;
    if (s.fleetManaged) {
      // Host owns the worktree; we only own the watcher.
      await s.fleetWatcher?.close();
      s.fleetWatcher = undefined;
      s.emitChange = undefined;
      return;
    }
    if (s.fetchTimer) {
      clearInterval(s.fetchTimer);
      s.fetchTimer = undefined;
    }
    if (s.autoCommitTimer) {
      clearTimeout(s.autoCommitTimer);
      s.autoCommitTimer = undefined;
    }
    this.teardownManagedCredential(handle.id, s);
  }

  private async _sync(handle: ConnectorHandle, _options?: SyncOptions): Promise<SyncResult> {
    const s = handle.state as GitState;
    if (s.fleetManaged) {
      // Host's GitFleetService owns periodic fetch/pull. Returning a no-op
      // SyncResult matches the sharepoint/webdav fleet pattern.
      this.log.debug("sync skipped — fleet-managed");
      return { filesWritten: 0, filesDeleted: 0, bytesTransferred: 0 };
    }
    this.log.debug("sync (pull) start", { branch: s.activeBranch });
    try {
      await git(["pull", "origin", s.activeBranch], handle.mountPath!, 60_000);
      await syncSubmodules(handle.mountPath!, this.log);
      this.log.debug("sync ok");
    } catch (err) {
      this.log.error("sync failed", err, { branch: s.activeBranch });
      throw err;
    }
    return { filesWritten: 0, filesDeleted: 0, bytesTransferred: 0 };
  }

  // ── Watch ──────────────────────────────────────────────────────────────────

  override watch(
    handle: ConnectorHandle,
    callback: (event: ConnectorChangeEvent) => void,
    options?: WatchOptions,
  ): WatchHandle {
    const s = handle.state as GitState;

    if (s.fleetManaged) {
      // The fleet watcher was created in connectFleetManaged and already
      // dispatches through `s.emitChange`. Hook the caller's callback in and
      // detach on close — no auto-commit timer, no second chokidar instance.
      s.emitChange = callback;
      return {
        close: async () => {
          s.emitChange = undefined;
        },
      };
    }

    // Wrap callback to reset auto-commit timer on every file change
    s.emitChange = (event: ConnectorChangeEvent) => {
      callback(event);
      this.resetAutoCommitTimer(s, handle.mountPath!);
    };

    let fsWatcher: WatchHandle | null = null;
    let closed = false;

    const ignored = [...(options?.ignored ?? []), "**/.git/**"];
    createFsWatcher(handle.mountPath!, s.emitChange, { ...options, ignored })
      .then((w) => {
        // close() may have already run before creation resolved — dispose
        // immediately so the late watcher does not leak.
        if (closed) void w.close();
        else fsWatcher = w;
      })
      .catch((err) => {
        this.log.warn("watcher creation failed", {
          error: err instanceof Error ? err.message : String(err),
        });
      });

    return {
      close: async () => {
        closed = true;
        s.emitChange = undefined;
        await fsWatcher?.close();
      },
    };
  }

  // ── Session lifecycle ──────────────────────────────────────────────────────

  override async onHibernate(handle: ConnectorHandle): Promise<string | undefined> {
    this.log.info("hibernate");
    const s = handle.state as GitState;
    if (s.fleetManaged) {
      // Host GitFleetService schedules pushes via its idle-debounce timer.
      return "fleet-managed";
    }
    const lc = s.lifecycle;
    if (!lc.commitOnHibernate) return;

    const result = await commitAll(handle.mountPath!, `skaile: hibernate snapshot`);
    if (!result) return "Nothing to commit.";

    const sha = await git(["rev-parse", "--short", "HEAD"], handle.mountPath!);

    if (lc.pushOnHibernate) {
      try {
        await git(["push", "origin", s.activeBranch], handle.mountPath!, 60_000);
        this.log.info("push ok", { branch: s.activeBranch });
      } catch (err) {
        this.log.error("push failed", err, { branch: s.activeBranch });
        throw err;
      }
    }

    return `Committed ${sha} on ${s.activeBranch}${lc.pushOnHibernate ? " (pushed)" : ""}`;
  }

  override async onSessionClose(handle: ConnectorHandle): Promise<string | undefined> {
    this.log.info("session closed");
    const s = handle.state as GitState;
    if (s.fleetManaged) {
      // Host's GitFleetService detaches the worktree when its session refcount
      // reaches zero; merge/push are its job, not ours.
      return "fleet-managed";
    }
    const lc = s.lifecycle;
    const details: string[] = [];

    try {
      // Commit any remaining work on the session branch
      const commitResult = await commitAll(handle.mountPath!, `skaile: session close`);
      if (commitResult) {
        const sha = await git(["rev-parse", "--short", "HEAD"], handle.mountPath!);
        details.push(`Committed ${sha}`);
      }

      // Push session branch
      if (lc.pushOnClose || lc.mergeOnClose) {
        await git(["push", "origin", s.activeBranch], handle.mountPath!, 60_000);
        details.push(`Pushed ${s.activeBranch}`);
      }

      // Merge session branch into base branch
      if (lc.mergeOnClose && s.activeBranch !== s.branch) {
        await git(["checkout", s.branch], handle.mountPath!);
        await git(["pull", "origin", s.branch], handle.mountPath!, 60_000);
        await syncSubmodules(handle.mountPath!, this.log);
        try {
          await git(["merge", s.activeBranch], handle.mountPath!);
        } catch {
          const resolved = await this.resolveConflictsOrAbort(s, handle.mountPath!, "merge");
          if (resolved) {
            details.push("Resolved merge conflicts (created .conflict files)");
          }
        }

        if (lc.pushOnClose) {
          await git(["push", "origin", s.branch], handle.mountPath!, 60_000);
        }

        details.push(`Merged ${s.activeBranch} into ${s.branch}`);
      }

      return details.join("; ") || undefined;
    } finally {
      // Tier-2: always tear down the agent-facing credential, including when
      // a final push fails. The runner also tears connectors down on container
      // shutdown, but the host may invoke `onSessionClose` first.
      this.teardownManagedCredential(handle.id, s);
    }
  }

  // ── Private: managed gitconfig (Tier-2 credential exposure) ──────────────

  /**
   * Scope the credential helper to the sanitized full repository URL.
   * Userinfo, query, and fragment data must never enter managed gitconfig.
   */
  private deriveCredentialScope(url: string): string {
    const u = new URL(url);
    return `${u.protocol}//${u.host}${u.pathname}`;
  }

  /**
   * Acquire the initial credential for a connector.
   *
   * `auth:` accepts the post-2026-05-06 grammar:
   *   - `backend`         → request a token from the platform mediator
   *                         (preferred for any platform-backed session).
   *   - `pat:env:NAME`    → static token from the secrets chain
   *                         (standalone CLI / non-platform contexts).
   *
   * Anonymous connectors (`auth` unset) succeed without a token — the
   * `git clone`/`git pull` calls still work for public repos.
   *
   * @returns The parsed auth ref, the resolved token (when any), and the
   *   `expiresAt` timestamp surfaced by the mediator (used to schedule a
   *   refresh tick when the credential is exposed to the agent CLI).
   */
  private async acquireInitialToken(
    declaration: ConnectorDeclaration,
    secrets: SecretProvider | undefined,
    tokenMediator: ConnectorTokenMediator | undefined,
  ): Promise<{
    authRef: MountAuthRef | undefined;
    token: string | undefined;
    expiresAt: Date | null;
    scope?: string[];
  }> {
    if (!declaration.auth) {
      return { authRef: undefined, token: undefined, expiresAt: null };
    }
    const authRef = resolveAuthRef(declaration.auth, declaration.id);
    if (!authRef) {
      return { authRef: undefined, token: undefined, expiresAt: null };
    }

    if (authRef.kind === "backend") {
      if (!tokenMediator) {
        throw new Error(
          `Connector '${declaration.id}' uses 'auth: backend' but no token mediator is wired ` +
            `(standalone CLI does not have one — use 'pat:env:NAME' instead).`,
        );
      }
      const result = await tokenMediator({ connectorId: declaration.id, reason: "initial" });
      if (!result.ok) {
        this.log.error("backend mediator refused initial token", undefined, {
          connectorId: declaration.id,
          code: result.code,
          message: result.message,
        });
        throw new TokenMediatorError(result.message, result.code, declaration.id);
      }
      return {
        authRef,
        token: result.token,
        expiresAt: result.expiresAt ? new Date(result.expiresAt) : null,
        ...(result.scope ? { scope: result.scope } : {}),
      };
    }

    // PAT path — resolve via secrets chain when available, fall back to
    // process.env for standalone CLI contexts.
    const inner = authRef.value;
    let token: string | undefined;
    if (secrets) {
      token = secrets.resolve(inner);
    } else if (inner.startsWith("env:")) {
      token = process.env[inner.slice(4)];
    } else {
      token = inner;
    }
    if (!token) {
      throw new Error(
        `PAT auth ref '${authRef.raw}' did not resolve to a token. ` +
          `Provision '${authRef.value}' in the SecretProviderChain.`,
      );
    }
    return { authRef, token, expiresAt: null };
  }

  /**
   * Register a credential helper that *reports* why there is no credential,
   * after this connector failed to connect.
   *
   * Without it git finds no helper for the host and asks the terminal for a
   * username, so the agent sees `fatal: could not read Username for
   * 'https://github.com'` — which names neither the mount nor the cause, and
   * which only ever appears on a write, since reads against a public remote
   * keep succeeding. The helper written here makes git stop with the mediator's
   * actual refusal text instead.
   *
   * Entirely best-effort: it runs on the failure path and must never mask the
   * connect error that is already on its way up.
   */
  private registerUnavailableCredentialHelper(
    declaration: ConnectorDeclaration,
    url: string,
    err: unknown,
  ): void {
    try {
      if (!url.startsWith("https://") && !url.startsWith("http://")) return;
      const gitconfigPath = process.env.GIT_CONFIG_GLOBAL;
      if (!gitconfigPath) return;

      const skaileDir = dirname(gitconfigPath);
      const credentialsPath = join(skaileDir, "git-credentials");
      const urlPrefix = this.deriveCredentialScope(url);
      const helperScriptPath = join(skaileDir, `credential-helper-${declaration.id}.sh`);
      const refreshAttemptPath = join(skaileDir, `refresh-attempt-${declaration.id}`);
      // A git failure message can carry the auth URL (execFile puts the whole
      // argv in it). The agent's terminal is not a place to leak a token.
      const reason = redactUrlAuth(err instanceof Error ? err.message : String(err));

      this.ensureAttemptStamp(declaration.id, refreshAttemptPath);
      writeHelperScript(
        helperScriptPath,
        renderCredentialHelperScript({
          credentialsPath,
          refreshFlagPath: join(skaileDir, "refresh-request"),
          credentialTag: `# skaile-mount: ${declaration.id}`,
          refreshAttemptPath,
          unavailableReason: reason,
        }),
      );
      writeUnavailableMountBlock({
        mountId: declaration.id,
        urlPrefix,
        gitconfigPath,
        credentialsPath,
        helperScriptPath,
      });
      this.log.warn("registered unavailable credential helper for failed mount", {
        connectorId: declaration.id,
        urlPrefix,
      });
    } catch (writeErr) {
      this.log.warn("failed to register unavailable credential helper", {
        connectorId: declaration.id,
        error: writeErr instanceof Error ? writeErr.message : String(writeErr),
      });
    }
  }

  /**
   * Best-effort pre-creation of the helper's refresh-attempt stamp. A missing
   * stamp only costs the cooldown (the helper dances on every git op instead of
   * once per window), so a failure here is never worth surfacing.
   */
  private ensureAttemptStamp(connectorId: string, stampPath: string): void {
    try {
      ensureRefreshAttemptStamp(stampPath);
    } catch (err) {
      this.log.warn("managed-gitconfig: could not pre-create refresh-attempt stamp", {
        connectorId,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  /**
   * Best-effort removal of the unavailable-mount blocks that would answer this
   * connector's git operations: its own from a previous failed session, and any
   * left under the same repository scope by a sibling mount that failed earlier
   * in this `connectAll` (git matches helpers by URL, not by mount id).
   */
  private clearUnavailableCredentialHelper(connectorId: string, url: string): void {
    const gitconfigPath = process.env.GIT_CONFIG_GLOBAL;
    if (!gitconfigPath) return;
    let urlPrefix: string | undefined;
    if (url.startsWith("https://") || url.startsWith("http://")) {
      try {
        urlPrefix = this.deriveCredentialScope(url);
      } catch {
        // Unparseable remote: fall back to clearing this mount's own block.
      }
    }
    try {
      removeUnavailableMountBlock({
        mountId: connectorId,
        gitconfigPath,
        ...(urlPrefix !== undefined ? { urlPrefix } : {}),
      });
    } catch (err) {
      this.log.warn("failed to clear unavailable credential helper", {
        connectorId,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  private exposeManagedCredential(
    declaration: ConnectorDeclaration,
    state: GitState,
    url: string,
    initial: {
      authRef: MountAuthRef | undefined;
      token: string | undefined;
      expiresAt: Date | null;
      scope?: string[];
    },
  ): void {
    const token = initial.token;
    if (!token) {
      this.log.warn("managed-gitconfig: skipped — no token resolved", {
        connectorId: declaration.id,
      });
      return;
    }
    if (!url.startsWith("https://") && !url.startsWith("http://")) {
      this.log.warn("managed-gitconfig: skipped — non-http remote", {
        connectorId: declaration.id,
        url,
      });
      return;
    }

    const gitconfigPath = process.env.GIT_CONFIG_GLOBAL;
    if (!gitconfigPath) {
      this.log.warn("managed-gitconfig: GIT_CONFIG_GLOBAL not set — agent CLI auth will not work", {
        connectorId: declaration.id,
      });
      return;
    }
    const skaileDir = dirname(gitconfigPath);
    const credentialsPath = join(skaileDir, "git-credentials");
    const urlPrefix = this.deriveCredentialScope(url);

    // Step 9a: write a per-connector credential-helper script so the agent's
    // interactive `git` can refresh stale tokens mid-401 by signalling the
    // runner via a refresh-flag file. The runner watches that flag and
    // re-mints tokens on `change`. The script is per-connector (not per-
    // workspace) because each one carries its own credentialsPath + tag.
    const refreshFlagPath = join(skaileDir, "refresh-request");
    const helperScriptPath = join(skaileDir, `credential-helper-${declaration.id}.sh`);
    const refreshAttemptPath = join(skaileDir, `refresh-attempt-${declaration.id}`);
    const credentialTag = `# skaile-mount: ${declaration.id}`;
    const scriptBody = renderCredentialHelperScript({
      credentialsPath,
      refreshFlagPath,
      credentialTag,
      // Passed explicitly on both call sites so the failure path and this one
      // never disagree about which stamp file bounds this mount's dance.
      refreshAttemptPath,
    });
    // The stamp must exist and be group-writable before the helper (agent uid)
    // can ever write it — `.skaile/` belongs to the runner.
    this.ensureAttemptStamp(declaration.id, refreshAttemptPath);
    let helperWritten = true;
    try {
      writeHelperScript(helperScriptPath, scriptBody);
    } catch (err) {
      helperWritten = false;
      this.log.warn(
        "managed-gitconfig: failed to write credential-helper script -- falling back to static helper",
        {
          connectorId: declaration.id,
          error: err instanceof Error ? err.message : String(err),
        },
      );
    }

    writeMountBlock({
      mountId: declaration.id,
      urlPrefix,
      token,
      gitconfigPath,
      credentialsPath,
      // Only point at the script when it was actually written. Pointing at an
      // unwritten path is not a fallback: the file may still hold a previous
      // session's *unavailable* body, which would refuse this live credential
      // with a stale reason. Omitting it selects the static `store --file=`
      // form, which reads the credential line this call is about to write.
      ...(helperWritten ? { helperScriptPath } : {}),
    });

    state.managedGitconfig = { gitconfigPath, credentialsPath, urlPrefix };

    const providerKind = initial.authRef?.kind ?? "pat";
    const isRefreshable = providerKind === "backend" && initial.expiresAt !== null;

    // Spec'd schema: kind=mount, subkind=git, instance=<id>, data.event=mint,
    // data.tier=2, data.provider, data.scope. PAT path is static — flag the
    // warning so operators see "no refresh" in the logs.
    this.log.info("mint", {
      event: "mint",
      origin: "tier-2",
      tier: 2,
      provider: providerKind,
      scope: urlPrefix,
      expiresAt: initial.expiresAt ? initial.expiresAt.toISOString() : null,
      ...(initial.scope ? { permissions: initial.scope } : {}),
      ...(isRefreshable ? {} : { warning: "static-credential" }),
    });

    // Backend-mediated tokens with finite expiry → schedule the rotation
    // tick. The 5-minute lead lets the long-push 401 retry path re-read the
    // new token via `credential.helper store` before the in-flight token
    // expires.
    if (isRefreshable && state.tokenMediator && initial.expiresAt) {
      this.scheduleRefresh(declaration.id, state, initial.expiresAt);
    }
  }

  /**
   * Schedule the next refresh of the credential file.
   *
   * Cancels any existing timer first so re-connects (and the test harness)
   * do not double-fire. Uses a 5-minute lead per spec; if the lead is
   * negative (very short-lived test tokens) we fall back to a tiny delay
   * so the timer still fires in deterministic order.
   */
  private scheduleRefresh(connectorId: string, state: GitState, expiresAt: Date): void {
    if (state.refreshTimer) {
      clearTimeout(state.refreshTimer);
      state.refreshTimer = undefined;
    }
    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    const delay = Math.max(1_000, expiresAt.getTime() - Date.now() - FIVE_MINUTES_MS);
    state.refreshTimer = setTimeout(() => {
      this.refreshManagedCredential(connectorId, state).catch((err) => {
        this.log.error("managed-gitconfig refresh failed", err, { connectorId });
      });
    }, delay);
  }

  private async refreshManagedCredential(connectorId: string, state: GitState): Promise<void> {
    if (!state.tokenMediator) return;

    const result = await state.tokenMediator({ connectorId, reason: "refresh" });
    if (!result.ok) {
      this.log.error("backend mediator refused refresh", undefined, {
        connectorId,
        code: result.code,
        message: result.message,
      });
      // Best-effort retry once after 30s on transient errors. On a second
      // failure we surface via the existing log channel and stop scheduling
      // — the next git operation will hit a 401 and the manager will
      // restart the connector via its existing failure path.
      if (result.code === "backend-error" || result.code === "provider-error") {
        if (state.refreshTimer) {
          clearTimeout(state.refreshTimer);
          state.refreshTimer = undefined;
        }
        state.refreshTimer = setTimeout(() => {
          this.refreshManagedCredential(connectorId, state).catch((err) => {
            this.log.error("managed-gitconfig refresh retry failed", err, { connectorId });
          });
        }, 30_000);
      }
      return;
    }

    state.token = result.token;
    if (state.managedGitconfig) {
      atomicReplaceCredential({
        mountId: connectorId,
        urlPrefix: state.managedGitconfig.urlPrefix,
        token: result.token,
        credentialsPath: state.managedGitconfig.credentialsPath,
      });
    } else if (state.targetDir) {
      // Tier-1: the URL token is the sole credential — re-bake it.
      await this.rebakeRemoteAuth(state.targetDir, state);
    }

    this.log.info("refresh", {
      event: "refresh",
      tier: state.managedGitconfig ? 2 : 1,
      provider: "backend",
      scope: state.managedGitconfig?.urlPrefix ?? state.url,
      expiresAt: result.expiresAt,
      ...(result.scope ? { permissions: result.scope } : {}),
    });

    if (result.expiresAt) {
      this.scheduleRefresh(connectorId, state, new Date(result.expiresAt));
    }
  }

  private teardownManagedCredential(connectorId: string, state: GitState): void {
    if (state.refreshTimer) {
      clearTimeout(state.refreshTimer);
      state.refreshTimer = undefined;
    }
    // Tier-1 has no gitconfig block to remove, but the mediator ref must still
    // be dropped from the disconnected handle's state.
    state.tokenMediator = undefined;
    if (!state.managedGitconfig) return;
    try {
      removeMountBlock({
        mountId: connectorId,
        gitconfigPath: state.managedGitconfig.gitconfigPath,
        credentialsPath: state.managedGitconfig.credentialsPath,
      });
      this.log.info("teardown", {
        event: "teardown",
        tier: 2,
        scope: state.managedGitconfig.urlPrefix,
      });
    } catch (err) {
      this.log.warn("managed-gitconfig: teardown best-effort failed", {
        connectorId,
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      state.managedGitconfig = undefined;
      state.tokenMediator = undefined;
    }
  }

  // ── Private: in-place git init ───────────────────────────────────────────

  /**
   * Bring a populated, non-git directory under git tracking without
   * touching the working tree. Runs only on the "dir populated, no `.git`"
   * branch of `connect()` — exactly when a previous connect aborted before
   * `.git` was written, or when the bind-mounted host dir's `.git` FILE
   * pointed outside the container.
   *
   * The intent is to preserve whatever the agent last had on disk. We
   * therefore:
   *   - `git init` — creates `.git/`; does not modify any existing file.
   *   - `git remote add origin <authUrl>` — wires up the remote so the
   *     normal lifecycle hooks (`pushOnHibernate`, `pushAfterCommit`)
   *     have a target.
   *
   * Deliberately omitted:
   *   - `fetch` — would touch the network on every recreate. The lifecycle
   *     handles sync explicitly (periodic `fetch`/`pull` via
   *     `sync.autoPull`, push on hibernate, etc.).
   *   - `checkout -f` / `reset --hard` / `clean -fd` — would discard the
   *     user's last on-disk state. The agent or autocommit reconciles
   *     with the remote later.
   *
   * Token persistence: the auth URL gets written into `.git/config`
   * exactly like the `git clone <authUrl>` path does — token-rotation
   * handling is external to this method (managed-gitconfig credential
   * helper refresh in Tier-2 connectors).
   */
  private async initInPlace(targetDir: string, authUrl: string): Promise<void> {
    await git(["init"], targetDir, 10_000);
    // Replace any existing origin (idempotent across re-connects).
    try {
      await git(["remote", "remove", "origin"], targetDir, 5_000);
    } catch {
      /* no existing origin — fine */
    }
    await git(["remote", "add", "origin", authUrl], targetDir, 5_000);
  }

  /**
   * Count `refs/heads/*` on `authUrl`, optionally narrowed to one `branch`.
   *
   * Returns `null` when the probe itself fails (network, auth, bad URL).
   * "Cannot tell" must never collapse into "no refs": the empty-remote
   * fallback WRITES to origin, and every caller therefore treats `null` as
   * "assume populated" and falls through to the path that fails loudly.
   */
  private async countRemoteHeads(
    targetDir: string,
    authUrl: string,
    branch?: string,
  ): Promise<number | null> {
    try {
      const out = await runGit(
        branch === undefined
          ? [...gitPrefix(targetDir), "ls-remote", "--heads", authUrl]
          : [...gitPrefix(targetDir), "ls-remote", "--heads", authUrl, `refs/heads/${branch}`],
        { timeout: 30_000 },
      );
      return out.length === 0 ? 0 : out.split("\n").length;
    } catch (err) {
      this.log.warn("ls-remote probe failed — assuming a populated remote", {
        error: redactUrlAuth(err instanceof Error ? err.message : String(err)),
      });
      return null;
    }
  }

  /**
   * Bootstrap a checkout against a remote that has no refs at all — the state
   * of a GitHub repo created without "Add a README".
   *
   * A plain `git clone` is NOT a substitute: cloning a ref-less remote leaves
   * local HEAD on git's `init.defaultBranch` (`master`), not the declared
   * branch, so the branch is created explicitly here.
   *
   * The root commit is created REGARDLESS of `access`, and only the push to
   * origin is gated on it. An unborn HEAD is not a stable resting state: the
   * next connect sees `.git` with no resolvable HEAD, routes to
   * `bootstrapFromOrigin`, and its `fetch origin <branch>` dies on the still
   * ref-less remote — turning a fixed first connect into a broken second one.
   * A local commit costs a read-only connector nothing (it is never pushed)
   * and makes reconnect take the cheap "use as-is" path.
   *
   * With the push done, `origin/<branch>` exists for everything downstream —
   * notably `checkoutSessionBranch`, whose publish-on-create push needs a
   * commit to point at.
   *
   * Invariant: this method never throws on a failed push. The session must
   * come up with a usable checkout even when origin is unreachable — the
   * lifecycle hooks (`pushAfterCommit`, hibernate/close push) publish later.
   */
  private async bootstrapEmptyRemote(
    targetDir: string,
    branch: string,
    authUrl: string,
    access: ConnectorDeclaration["access"],
  ): Promise<void> {
    await this.initInPlace(targetDir, authUrl);
    // `git init -b` would do this in one step but only from git 2.28;
    // symbolic-ref on an unborn HEAD is equivalent and has no version floor.
    await git(["symbolic-ref", "HEAD", `refs/heads/${branch}`], targetDir, 5_000);

    await git(
      [
        "-c",
        `user.name=${BOOTSTRAP_COMMIT_NAME}`,
        "-c",
        `user.email=${BOOTSTRAP_COMMIT_EMAIL}`,
        "commit",
        "--allow-empty",
        "-m",
        "chore: initialize repository",
      ],
      targetDir,
      30_000,
    );

    if (access === "read-only") {
      this.log.info("empty remote is read-only — branch created locally, nothing pushed", {
        branch,
      });
      return;
    }

    try {
      await git(["push", "-u", "origin", branch], targetDir, 60_000);
      this.log.info("empty remote initialised on origin", { branch });
      return;
    } catch (err) {
      this.log.warn("empty-remote publish push failed; deferring to lifecycle push", {
        branch,
        error: redactUrlAuth(err instanceof Error ? err.message : String(err)),
      });
    }

    // A concurrent session may have published the branch between our probe and
    // our push. Adopt theirs rather than keeping an unrelated local root commit
    // that every later push would have to be forced over.
    const publishedElsewhere = await this.countRemoteHeads(targetDir, authUrl, branch);
    if (publishedElsewhere === null || publishedElsewhere === 0) return;
    try {
      this.log.info("origin gained the branch concurrently — adopting it", { branch });
      await this.bootstrapFromOrigin(targetDir, branch, authUrl);
    } catch (err) {
      this.log.warn("adopting the concurrently-created branch failed; keeping local branch", {
        branch,
        error: redactUrlAuth(err instanceof Error ? err.message : String(err)),
      });
    }
  }

  /**
   * True when `<targetDir>/.git` contains at least one resolvable commit
   * (i.e. `HEAD` points at a real object). False when `.git` is an empty
   * shell — e.g. just-initialised but never fetched, or all refs deleted.
   *
   * Used by `connect()` to tell the difference between a working agent
   * checkout (preserve, don't touch) and a half-initialised one
   * (bootstrap by fetching from origin).
   */
  private async isInitialisedCheckout(targetDir: string): Promise<boolean> {
    try {
      await git(["rev-parse", "HEAD"], targetDir, 5_000);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * One-shot bootstrap path for the "`.git` exists but is empty" state.
   *
   * Runs `fetch --depth=1 origin <branch>` followed by `checkout -B
   * <branch> origin/<branch>` so the working tree gets the remote
   * content. Before the checkout, any untracked file whose path also
   * exists in the remote tree is renamed to `<path>.local` so the user
   * (or previous agent run) does not lose data. Other untracked files
   * are left in place.
   *
   * Deliberately not destructive: we never `clean -fd` and never
   * `reset --hard`. Files that do not conflict with the remote stay
   * exactly where they are.
   *
   * Auth-URL handling matches `initInPlace`: a pre-existing `origin`
   * remote is replaced with the authed URL so the fetch works even
   * after token rotation. This persists the token in `.git/config`
   * the same way `git clone <authUrl>` does — token-rotation handling
   * is external to this connector method.
   */
  private async bootstrapFromOrigin(
    targetDir: string,
    branch: string,
    authUrl: string,
  ): Promise<void> {
    // Ensure origin points at the current authed URL — handles the case
    // where the previous `initInPlace` wrote a stale token, or the
    // remote was never configured at all.
    try {
      await git(["remote", "remove", "origin"], targetDir, 5_000);
    } catch {
      /* no existing origin — fine */
    }
    await git(["remote", "add", "origin", authUrl], targetDir, 5_000);
    await git(["fetch", "--depth=1", "origin", branch], targetDir, 120_000);
    await this.preserveConflictingUntracked(targetDir, branch);
    await git(["checkout", "-B", branch, `origin/${branch}`], targetDir, 30_000);
    await syncSubmodules(targetDir, this.log);
    try {
      await git(["branch", `--set-upstream-to=origin/${branch}`, branch], targetDir, 5_000);
    } catch {
      /* best-effort */
    }
  }

  /**
   * Walk the remote tree at `origin/<branch>` and rename any untracked
   * working-tree file whose path also exists there. Renames go to
   * `<path>.local`; on `.local` collision a millisecond suffix is
   * appended so nothing is overwritten.
   *
   * This makes the subsequent `git checkout -B <branch> origin/<branch>`
   * safe: without it, `checkout` aborts with "untracked working tree
   * files would be overwritten" the moment any local file collides with
   * a tracked path on the remote.
   *
   * Files in the local tree whose paths are NOT in the remote tree are
   * left untouched — they are pure local additions and survive the
   * bootstrap.
   */
  private async preserveConflictingUntracked(targetDir: string, branch: string): Promise<void> {
    let remoteFiles: string[] = [];
    try {
      remoteFiles = (
        await git(["ls-tree", "-r", `origin/${branch}`, "--name-only"], targetDir, 30_000)
      )
        .split("\n")
        .filter(Boolean);
    } catch (err) {
      this.log.warn("ls-tree failed — skipping untracked preservation", {
        branch,
        error: err instanceof Error ? err.message : String(err),
      });
      return;
    }
    for (const relPath of remoteFiles) {
      const fullPath = join(targetDir, relPath);
      if (!(await pathExists(fullPath))) continue;
      let dest = `${fullPath}.local`;
      if (await pathExists(dest)) {
        dest = `${fullPath}.local.${Date.now()}`;
      }
      try {
        await withFsDeadline(`rename ${fullPath}`, () => rename(fullPath, dest));
        this.log.info("preserved local file as .local", { path: relPath, dest });
      } catch (err) {
        this.log.warn("failed to preserve local file — checkout may fail", {
          path: relPath,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }

  // ── Private: auth URL ─────────────────────────────────────────────────────

  private authUrl(s: GitState): string {
    if (s.token && s.url.startsWith("https://")) {
      const urlObj = new URL(s.url);
      urlObj.username = "x-access-token";
      urlObj.password = s.token;
      return urlObj.toString();
    }
    return s.url;
  }

  /**
   * Align `origin` with the exact sanitized URL used by the Tier-2 helper.
   * Git does not normalize query/fragment data, path case, or `.git` aliases
   * when matching `credential.<url>` sections, so the two strings must agree.
   * The declared mount source remains authoritative over an existing remote.
   */
  private async alignRemoteWithCredentialScope(
    targetDir: string,
    declaredUrl: string,
  ): Promise<string> {
    const credentialScopeUrl = this.deriveCredentialScope(declaredUrl);
    const remotes = (await git(["remote"], targetDir, 5_000)).split(/\r?\n/);
    if (!remotes.includes("origin")) {
      await git(["remote", "add", "origin", credentialScopeUrl], targetDir, 5_000);
    } else if (
      (await git(["remote", "get-url", "origin"], targetDir, 5_000)) !== credentialScopeUrl
    ) {
      await git(["remote", "set-url", "origin", credentialScopeUrl], targetDir, 5_000);
    }
    return credentialScopeUrl;
  }

  /**
   * Tier-1 only: rewrite `origin` so it carries the token freshly minted on
   * this connect. The "use as-is" wake path leaves whatever token the first
   * connect baked in — long-dead for short-lived (GitHub App) tokens. Without
   * a Tier-2 helper this URL token is the sole credential, so it must be
   * refreshed here. No-op for anonymous (no token) or non-https remotes.
   */
  private async rebakeRemoteAuth(targetDir: string, state: GitState): Promise<void> {
    if (!state.token || !state.url.startsWith("https://")) return;
    try {
      await git(["remote", "set-url", "origin", this.authUrl(state)], targetDir, 5_000);
    } catch (err) {
      this.log.warn("failed to refresh inline token on origin", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  // ── Periodic fetch ─────────────────────────────────────────────────────────

  private startPeriodicFetch(s: GitState, mountPath: string): void {
    if (s.fetchTimer) clearInterval(s.fetchTimer);

    const intervalMs = s.sync.fetchIntervalSec * 1000;
    s.fetchTimer = setInterval(() => {
      // The tick body is async now, so a mount slow enough to outrun the
      // interval would stack overlapping fetch/rebase/pull runs on one
      // checkout. Skip instead: the next interval retries anyway, and a
      // queue of git children on a wedged mount helps nobody.
      if (s.fetchInFlight) {
        this.log.warn("periodic fetch skipped — previous tick still running", {
          branch: s.activeBranch,
          intervalMs,
        });
        return;
      }
      if (s.disposed) return;
      s.fetchInFlight = true;
      // Nothing awaits this promise, so it must never reject: an unhandled
      // rejection in a timer callback takes the process down, where the old
      // synchronous body merely threw into its own try/catch.
      void this.runPeriodicFetch(s, mountPath)
        .catch((err) => {
          // Periodic fetch is best-effort.
          this.log.debug("periodic fetch failed", {
            error: err instanceof Error ? err.message : String(err),
          });
        })
        .finally(() => {
          s.fetchInFlight = false;
        });
    }, intervalMs);
  }

  /** One periodic-fetch tick. Bails out at each resumption point once disconnected. */
  private async runPeriodicFetch(s: GitState, mountPath: string): Promise<void> {
    await git(["fetch", "origin"], mountPath, 60_000);
    if (s.disposed) return;
    if (s.sync.autoRebase && s.activeBranch !== s.branch) {
      try {
        await git(["rebase", `origin/${s.branch}`], mountPath, 60_000);
      } catch {
        await this.resolveConflictsOrAbort(s, mountPath, "rebase");
      }
      if (s.disposed) return;
      await syncSubmodules(mountPath, this.log);
    } else if (s.sync.autoPull) {
      await git(["pull", "origin", s.activeBranch], mountPath, 60_000);
      if (s.disposed) return;
      await syncSubmodules(mountPath, this.log);
    }
  }

  // ── Conflict resolution ──────────────────────────────────────────────────

  /**
   * Attempt to resolve merge/rebase conflicts by keeping "ours" and copying
   * "theirs" to a .conflict file. If resolution fails entirely, abort.
   * Returns true if conflicts were resolved, false if aborted.
   */
  private async resolveConflictsOrAbort(
    s: GitState,
    mountPath: string,
    mode: "rebase" | "merge",
  ): Promise<boolean> {
    try {
      const conflicted = (await git(["diff", "--name-only", "--diff-filter=U"], mountPath))
        .split("\n")
        .filter(Boolean);

      if (conflicted.length === 0) return false;

      for (const file of conflicted) {
        await this.resolveFileConflict(mountPath, file);
      }

      // Continue the rebase/merge
      if (mode === "rebase") {
        await git(["-c", "core.editor=true", "rebase", "--continue"], mountPath, 30_000);
      } else {
        await git(["commit", "--no-edit"], mountPath);
      }

      // Emit change events for conflict files
      for (const file of conflicted) {
        const ext = file.includes(".") ? file.slice(file.lastIndexOf(".")) : "";
        const base = file.includes(".") ? file.slice(0, file.lastIndexOf(".")) : file;
        s.emitChange?.({
          path: `${base}.conflict${ext}`,
          action: "create",
          source: "sync",
        });
      }

      return true;
    } catch {
      // Abort if resolution fails
      try {
        if (mode === "rebase") {
          await git(["rebase", "--abort"], mountPath);
        } else {
          await git(["merge", "--abort"], mountPath);
        }
      } catch {
        /* already clean */
      }
      return false;
    }
  }

  /**
   * For a single conflicted file: keep "ours", save "theirs" as .conflict file.
   */
  private async resolveFileConflict(root: string, file: string): Promise<void> {
    const ext = file.includes(".") ? file.slice(file.lastIndexOf(".")) : "";
    const base = file.includes(".") ? file.slice(0, file.lastIndexOf(".")) : file;
    const conflictPath = `${base}.conflict${ext}`;

    try {
      // Extract "theirs" version (stage 3)
      const theirs = await git(["show", `:3:${file}`], root);
      await withFsDeadline(`write ${conflictPath}`, () =>
        writeFile(join(root, conflictPath), theirs),
      );
    } catch {
      // If we can't get theirs, copy the conflicted file as-is
      const fullPath = join(root, file);
      const content = await tryWithFsDeadline<string | null>(
        `read ${file}`,
        () => readFile(fullPath, "utf-8"),
        null,
      );
      if (content !== null) {
        await withFsDeadline(`write ${conflictPath}`, () =>
          writeFile(join(root, conflictPath), content),
        );
      }
    }

    try {
      // Keep "ours" version (stage 2)
      const ours = await git(["show", `:2:${file}`], root);
      await withFsDeadline(`write ${file}`, () => writeFile(join(root, file), ours));
    } catch {
      // Strip conflict markers, keeping our sections
      const fullPath = join(root, file);
      const content = await tryWithFsDeadline<string | null>(
        `read ${file}`,
        () => readFile(fullPath, "utf-8"),
        null,
      );
      if (content !== null) {
        await withFsDeadline(`write ${file}`, () =>
          writeFile(fullPath, this.stripConflictKeepOurs(content)),
        );
      }
    }

    await git(["add", file, conflictPath], root);
  }

  private stripConflictKeepOurs(content: string): string {
    return content.replace(/<<<<<<< .*\n/g, "").replace(/=======\n[\s\S]*?>>>>>>> .*\n/g, "");
  }

  // ── Auto-commit on idle ────────────────────────────────────────────────────

  private resetAutoCommitTimer(s: GitState, mountPath: string): void {
    if (s.lifecycle.autoCommitIdleMs <= 0) return;
    if (s.disposed) return;
    if (s.autoCommitTimer) clearTimeout(s.autoCommitTimer);
    s.autoCommitTimer = setTimeout(() => {
      s.autoCommitTimer = undefined;
      if (s.disposed) return;
      if (s.autoCommitInFlight) {
        // `git add -A` + `git status` walk the whole working tree; on a large
        // cloud-backed one a run can outlast the idle window. Re-arm for
        // another window instead of starting a second competing commit —
        // the pending changes are still there when it fires.
        this.log.warn("auto-commit skipped — previous run still in flight", {
          branch: s.activeBranch,
        });
        this.resetAutoCommitTimer(s, mountPath);
        return;
      }
      s.autoCommitInFlight = true;
      // Nothing awaits this promise — it must never reject (see startPeriodicFetch).
      void this.performAutoCommit(s, mountPath)
        .catch((err) => {
          // Auto-commit is best-effort.
          this.log.debug("auto-commit failed", {
            error: err instanceof Error ? err.message : String(err),
          });
        })
        .finally(() => {
          s.autoCommitInFlight = false;
        });
    }, s.lifecycle.autoCommitIdleMs);
  }

  private async performAutoCommit(s: GitState, mountPath: string): Promise<void> {
    try {
      const message = await this.generateCommitMessage(mountPath);
      if (s.disposed) return;
      const result = await commitAll(mountPath, message);
      if (!result || s.disposed) return;
      if (s.lifecycle.pushAfterCommit) {
        try {
          await git(["push", "origin", s.activeBranch], mountPath, 60_000);
        } catch {
          // Push is best-effort
        }
      }
    } catch {
      // Auto-commit is best-effort
    }
  }

  private async generateCommitMessage(mountPath: string): Promise<string> {
    try {
      await git(["add", "-A"], mountPath);
      const stat = await git(["diff", "--cached", "--stat"], mountPath);
      const lines = stat.split("\n").filter((l) => l.includes("|"));
      if (lines.length === 0) return "skaile: auto-save";
      const files = lines.map((l) => l.split("|")[0]!.trim());
      if (files.length === 1) return `Update ${files[0]}`;
      return `Update ${files.length} files`;
    } catch {
      return "skaile: auto-save";
    }
  }
}

/**
 * Creates a new GitConnector instance.
 * @returns Configured GitConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#git-connector-factory
 */
export function createConnector(): GitConnector {
  return new GitConnector();
}

/** Test-only surface — exposes the mode→config resolver for unit assertions. */
export const __test = { resolveGitConfig };
